import { Tooltip } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { FidcAlertModal } from 'v2/components/Shared/FidcAlertModal';
import { sendEventToGA } from 'v2/helpers/analytics';
import { numberToBRL } from 'v2/helpers/money';
import { useQuery } from 'v2/hooks/useQuery';
import { anticipationService } from 'v2/services/anticipation';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { financialStatementService } from 'v2/services/financialStatement/statement';

import AnticipationModal from './AnticipationModal';
import { MIN_VALUE_TO_ANTICIPATE, type AnticipationGroupInfo } from './AnticipationModal/constants';
import AnticipationPeriodModal from './AnticipationPeriodModal';
import { NO_PAID_INVOICE_MORE_THIRTY_DAYS_ERROR, type AnticipationData, type Props, HIGH_CHARGEBACK_ERROR } from './constants';
import SuggestionAnticipationModal from './SuggestionAnticipationModal';
import styles from '../styles';
import ValueCard from '../ValueCard';

function AnticipationCard({
  anticipationAccessData,
  modalAnticipateIsOpen,
  handleSetHasValueToAnticipate,
  updateTransferAvailable,
  handleToggleTransferModal,
  handleToggleAnticipateModal,
}: Props) {
  const classes = styles();

  const [loadingAvailableAnticipate, setLoadingAvailableAnticipate] = useState(true);
  const [anticipationData, setAnticipationData] = useState<AnticipationData>(null);
  const [loadingAnticipationData, setLoadingAnticipationData] = useState<boolean>(true);
  const [isOpenAnticipationPeriodModal, setIsOpenAnticipationPeriodModal] = useState<boolean>(false);
  const [isOpenSuggestionAnticipationModal, setIsOpenSuggestionAnticipationModal] = useState<boolean>(false);
  const [suggestionAnticipation, setSuggestionAnticipation] = useState<null | number>(null);
  const [modalGalaxyAlert, setModalGalaxyAlert] = useState(false);
  const [customerAnticipationGroup, setCustomerAnticipationGroup] = useState<AnticipationGroupInfo>(null);
  const [isTooltipOpen, setIsTooltipOpen] = useState<boolean | undefined>(undefined);

  const [availableAnticipateValue] = useObservable(() => anticipationService.getCurrentBalance(), []);
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);

  const params = useQuery();
  const actionQuery = params.get('a');

  const anticipationAccess = anticipationAccessData;
  const anticipationAccessIsCompleted = !!anticipationAccessData;

  const hasValueToAnticipate = availableAnticipateValue > MIN_VALUE_TO_ANTICIPATE;
  const anticipationBlocked = anticipationAccess?.status === 'blocked';
  const disableAnticipationFeatures = anticipationBlocked || !hasValueToAnticipate || !anticipationAccess;
  const disableAutomaticAnticipation =
    anticipationBlocked &&
    (anticipationAccess?.code === NO_PAID_INVOICE_MORE_THIRTY_DAYS_ERROR || anticipationAccess?.code === HIGH_CHARGEBACK_ERROR);

  const MESSAGE_NO_VALUE_TO_ANTICIPATE = `É preciso ter ao menos ${numberToBRL(MIN_VALUE_TO_ANTICIPATE)} disponível para poder antecipar`;

  const getDisabledMessageAnticipationFeatures = () => {
    if (anticipationBlocked) return anticipationAccess?.reason;
    if (!hasValueToAnticipate) return MESSAGE_NO_VALUE_TO_ANTICIPATE;
    return '';
  };

  const [loadAvailableAnticipate] = useCallbackObservable(() => {
    setLoadingAvailableAnticipate(true);

    return anticipationService.getAvailable().pipe(
      tap(({ data }) => {
        anticipationService.setCurrentBalance(user?.crossborder ? data.total_origin : data.total);
        handleSetHasValueToAnticipate(!!data.total_origin || !!data.total);
        setCustomerAnticipationGroup(data.anticipation_group);
        setLoadingAvailableAnticipate(false);
      }),
      catchError((error) => {
        toast.error(error);
        setLoadingAvailableAnticipate(false);
        return of(true);
      })
    );
  }, []);

  const [loadAnticipationData] = useCallbackObservable(() => {
    setLoadingAnticipationData(true);

    return financialStatementService.getAnticipationData().pipe(
      tap(({ data }) => {
        setAnticipationData(data);
        setLoadingAnticipationData(false);
      }),
      catchError((error) => {
        toast.error(error);
        setLoadingAnticipationData(false);
        return of(true);
      })
    );
  }, []);

  const [loadSuggestionAnticipation] = useCallbackObservable(() => {
    if (anticipationData?.credit_days === 30) {
      return anticipationService.getSuggestionAnticipation().pipe(
        tap(({ data }) => {
          setSuggestionAnticipation(data.anticipation_suggestion);
        }),
        catchError((error) => {
          toast.error(error);
          return of(true);
        })
      );
    }
    return null;
  }, [anticipationData]);

  const toggleAnticipationPeriodModal = () => setIsOpenAnticipationPeriodModal((prevState) => !prevState);

  const handleOpenAnticipationPeriodModal = () => {
    sendEventToGA({
      category: 'Novo Extrato',
      action: 'Botão',
      label: 'Alterar Período',
    });

    toggleAnticipationPeriodModal();
  };

  const handleOpenAnticipationModal = () => {
    sendEventToGA({
      category: 'Novo Extrato',
      action: 'Botão',
      label: 'Antecipar',
    });
    handleToggleAnticipateModal();
  };

  const handleToggleSuggestionAnticipationModal = () => {
    setIsOpenSuggestionAnticipationModal((prevState) => !prevState);
  };

  useEffect(() => {
    loadAvailableAnticipate();
    loadAnticipationData();
  }, [loadAvailableAnticipate, loadAnticipationData]);

  useEffect(() => {
    if (actionQuery !== 'anticipation') return;
    setIsTooltipOpen(false);
    if (!anticipationAccessIsCompleted) return;
    if (disableAnticipationFeatures) {
      setIsTooltipOpen(true);
      return;
    }
    handleOpenAnticipationModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionQuery, anticipationAccessIsCompleted, disableAnticipationFeatures]);

  useEffect(() => {
    if (actionQuery !== 'automatic-anticipation') return;
    toggleAnticipationPeriodModal();
  }, [actionQuery]);

  return (
    <ValueCard
      id='anticipation-card'
      title='Disponível para antecipação'
      isLoading={loadingAvailableAnticipate || !anticipationAccessIsCompleted}
      value={availableAnticipateValue}
      hint='Antecipação de faturas em lançamentos futuros'
      ctaCopy='Receba agora!'
      isCtaDisabled={disableAnticipationFeatures}
      ctaButtonTooltip={getDisabledMessageAnticipationFeatures()}
      isTooltipOpen={isTooltipOpen}
      ctaCallback={handleOpenAnticipationModal}
      ctaAccessControl='myeduzz:financeiro:solicitar-transferencia'
      bottomExtra={
        <div className={classes.containerExtra}>
          <span>
            Antecipação automática: <strong>{loadingAnticipationData ? '-' : anticipationData?.credit_days} dias</strong>
          </span>
          <Tooltip title={disableAutomaticAnticipation ? getDisabledMessageAnticipationFeatures() : ''} placement='top'>
            <span>
              <Button
                id='open-anticipation-modal'
                variant='text'
                className={classes.smallTextButton}
                onClick={handleOpenAnticipationPeriodModal}
                disabled={disableAutomaticAnticipation}
              >
                Alterar
              </Button>
            </span>
          </Tooltip>

          {modalAnticipateIsOpen && (
            <AnticipationModal
              anticipationData={anticipationData}
              isOpen={modalAnticipateIsOpen}
              setClose={handleToggleAnticipateModal}
              updateTransferAvailable={updateTransferAvailable}
              handleToggleTransferModal={handleToggleTransferModal}
              handleOpenFidcModal={() => setModalGalaxyAlert(true)}
              handleUpdateAvailableAmount={loadAvailableAnticipate}
              handleOpenSuggestionAnticipation={handleToggleSuggestionAnticipationModal}
              loadSuggestionAnticipation={loadSuggestionAnticipation}
              hasSuggestionAnticipation={!!suggestionAnticipation}
              anticipationGroupInfo={customerAnticipationGroup}
            />
          )}
          <FidcAlertModal
            isOpen={modalGalaxyAlert}
            onClose={() => {
              setModalGalaxyAlert(false);
              handleToggleTransferModal();
            }}
          />
          <SuggestionAnticipationModal
            open={isOpenSuggestionAnticipationModal}
            setClose={handleToggleSuggestionAnticipationModal}
            suggestionAnticipationValue={suggestionAnticipation}
            anticipationData={anticipationData}
            loadAnticipationData={loadAnticipationData}
          />
          <AnticipationPeriodModal
            open={isOpenAnticipationPeriodModal}
            setClose={toggleAnticipationPeriodModal}
            anticipationData={anticipationData}
            loadAnticipationData={loadAnticipationData}
          />
        </div>
      }
    />
  );
}

export default AnticipationCard;
